'use client';

import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Checkbox,
  Dialog,
  Fade,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { CustomButton } from '@/components/common/button';
import { useRBACOptional } from '@/contexts/RBACContext';
import { SessionContext } from '@/app/components/Session';
import { navData } from '@/app/components/navData';
import { BETA_ACCESS, useBetaAccess } from '@/hooks/useBetaAccess';
import { getOnboardingPreference, updateOnboardingPreference } from '@/services/userLoginApi';
import { FAMILIES } from './content';
import { TourFocusContext } from './blocks';
import { buildSearchIndex, searchIndex, SearchResults, type SearchEntry } from './search';
import {
  AccountSettingsStep,
  FinishStep,
  ModuleStep,
  OrganizationStep,
  PixelStep,
  SubscriptionStep,
  TeamStep,
  WalletStep,
  WelcomeStep,
  WorkflowStep,
  type ModuleGroup,
  type NavItemLike,
  type StepContext,
} from './steps';

// "Seen this login" lives in sessionStorage and "don't show again" is mirrored to
// localStorage; clearStorage() on logout wipes both, so the server preference is
// the source of truth on the next login.
const SEEN_THIS_SESSION_KEY = 'platform_onboarding_seen';
const DONT_SHOW_AGAIN_KEY = 'platform_onboarding_dont_show_again';

/** Dispatch on `window` to reopen the tour on demand (e.g. from the account menu). */
export const OPEN_PLATFORM_ONBOARDING_EVENT = 'open-platform-onboarding';

export function openPlatformOnboarding() {
  window.dispatchEvent(new Event(OPEN_PLATFORM_ONBOARDING_EVENT));
}

/**
 * The tour is for people who set the platform up: the organization owner and
 * sub-tenant admins (account_admin inside a sub-tenant). Everyone else never sees it.
 */
export function useCanSeePlatformOnboarding(): boolean {
  const rbac = useRBACOptional();
  if (!rbac || rbac.isLoading || !rbac.userContext) return false;
  if (rbac.isOrgOwner) return true;
  return rbac.hasRole('account_admin') && rbac.userContext.parent_account_id != null;
}

function readStorage(storage: 'local' | 'session', key: string): string | null {
  try {
    return (storage === 'local' ? localStorage : sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(storage: 'local' | 'session', key: string, value: string) {
  try {
    (storage === 'local' ? localStorage : sessionStorage).setItem(key, value);
  } catch {
    /* storage unavailable (private mode) — modal just may reappear */
  }
}

// ----------------------------------------------------------------------

type NavGroupLike = {
  subheader: string | { title: string };
  items: NavItemLike[];
};

type TourStep = {
  id: string;
  section: 'Getting started' | 'Modules' | 'Finish';
  label: string;
  icon: string;
  color?: string;
  render: () => ReactNode;
};

export type PlatformOnboardingModalProps = {
  /** Only evaluate/show once the session user is known and no other blocking gate is up. */
  enabled: boolean;
  /** The sidebar nav already filtered by the user's permissions — drives the module steps. */
  nav: NavGroupLike[];
};

/**
 * Post-login platform tour: Organization, Team & Access, Subscription, Wallet, one step
 * per module family the user can reach, account settings and a personalized checklist.
 * Shown once per login until the user ticks "Don't show again", which is saved via the
 * single `/api/onboardingPreference` API. Reopen anytime with `openPlatformOnboarding()`.
 */
export function PlatformOnboardingModal({ enabled, nav }: PlatformOnboardingModalProps) {
  const theme = useTheme();
  const router = useRouter();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const rbac = useRBACOptional();
  const eligible = useCanSeePlatformOnboarding();

  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [visited, setVisited] = useState<Set<number>>(() => new Set([0]));
  const [dontShowAgain, setDontShowAgain] = useState(false);
  // The preference is one-way (only `true` is ever saved), so once set the checkbox is locked.
  const [alreadyDismissed, setAlreadyDismissed] = useState(false);
  const [query, setQuery] = useState('');
  // Title of the item a search result pointed at; scrolled to and flashed on its step.
  const [focus, setFocus] = useState<{ title: string; nonce: number } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-open once per login unless dismissed for good.
  useEffect(() => {
    if (!enabled || !eligible || open) return;
    if (readStorage('session', SEEN_THIS_SESSION_KEY) || readStorage('local', DONT_SHOW_AGAIN_KEY)) return;

    let cancelled = false;
    getOnboardingPreference()
      .then((pref) => {
        if (cancelled) return;
        if (pref?.dont_show_again) {
          writeStorage('local', DONT_SHOW_AGAIN_KEY, '1');
          return;
        }
        setOpen(true);
      })
      .catch((err) => {
        // Informational only — fail open so a preference-API outage doesn't hide onboarding.
        console.warn('Onboarding preference fetch failed:', err);
        if (!cancelled) setOpen(true);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, eligible, open]);

  // Manual reopen from anywhere in the app.
  useEffect(() => {
    if (!eligible) return;
    const handler = () => {
      setStepIndex(0);
      setVisited(new Set([0]));
      const dismissed = Boolean(readStorage('local', DONT_SHOW_AGAIN_KEY));
      setDontShowAgain(dismissed);
      setAlreadyDismissed(dismissed);
      setQuery('');
      setFocus(null);
      setOpen(true);
    };
    window.addEventListener(OPEN_PLATFORM_ONBOARDING_EVENT, handler);
    return () => window.removeEventListener(OPEN_PLATFORM_ONBOARDING_EVENT, handler);
  }, [eligible]);

  const userContext = rbac?.userContext;
  const { user: sessionUser } = useContext(SessionContext);
  const isOwner = Boolean(userContext?.is_org_owner);
  // Sub-account members manage their own account's subscription/wallet, not the org's.
  const isSubAccountMember = Boolean(userContext) && !isOwner && userContext?.parent_account_id != null;

  // Blyn is limited to users granted blyn.view. Unlike the sidebar, the tour ignores sub-tenant
  // scope here, so granted users always get the Blyn step and mentions.
  const showBlyn = useBetaAccess(BETA_ACCESS.blynView);

  const modules: ModuleGroup[] = useMemo(() => {
    const groupKey = (group: NavGroupLike) =>
      (typeof group.subheader === 'string' ? group.subheader : group.subheader?.title ?? '').toUpperCase();
    const list = nav
      .map((group) => {
        const key = groupKey(group);
        if (key === 'BLYN' && !showBlyn) return null;
        return FAMILIES[key] && group.items?.length ? { key, items: group.items } : null;
      })
      .filter((m): m is ModuleGroup => m !== null);
    if (showBlyn && !list.some((m) => m.key === 'BLYN')) {
      const blyn = (navData as NavGroupLike[]).find((g) => groupKey(g) === 'BLYN');
      if (blyn?.items?.length) {
        // Keep sidebar order: Blyn sits right after Command Center.
        const at = list.findIndex((m) => m.key === 'COMMAND CENTER') + 1;
        list.splice(at, 0, { key: 'BLYN', items: blyn.items });
      }
    }
    return list;
  }, [nav, showBlyn]);

  const canOpen = useCallback((permission: string) => (rbac ? rbac.hasPermission(permission) : true), [rbac]);
  const canAny = useCallback((permissions: string[]) => (rbac ? rbac.hasAnyPermission(permissions) : true), [rbac]);

  const close = useCallback(() => {
    writeStorage('session', SEEN_THIS_SESSION_KEY, '1');
    setOpen(false);
    if (!dontShowAgain || readStorage('local', DONT_SHOW_AGAIN_KEY)) return;
    writeStorage('local', DONT_SHOW_AGAIN_KEY, '1');
    updateOnboardingPreference({ dont_show_again: true }).catch((err) => {
      console.warn('Saving onboarding preference failed:', err);
    });
  }, [dontShowAgain]);

  const goTo = useCallback(
    (path: string) => {
      close();
      router.push(path);
    },
    [close, router]
  );

  const stepsRef = useRef<TourStep[]>([]);
  const jumpToStep = useCallback((id: string) => {
    const i = stepsRef.current.findIndex((s) => s.id === id);
    if (i >= 0) setStepIndex(i);
  }, []);

  // rbac-svc can omit org_name (e.g. owners whose /me/context has no account); fall back to the
  // user's company, then — for owners only, where it is the root account — the account name.
  const orgName =
    userContext?.org_name?.trim() ||
    sessionUser?.company?.trim() ||
    (isOwner ? userContext?.account_name?.trim() : undefined) ||
    undefined;

  // Same resolution as the Subscription page: non-owners see the org owner's plan.
  const pricingUserId = useMemo(() => {
    const own = parseInt(String(sessionUser?.userID ?? ''), 10);
    const ownId = Number.isFinite(own) ? own : null;
    const ownerId = userContext?.owner_user_id ?? null;
    return !isOwner && ownerId ? ownerId : ownId;
  }, [sessionUser?.userID, userContext?.owner_user_id, isOwner]);

  const ctx: StepContext = useMemo(
    () => ({
      orgName,
      pricingUserId,
      showBlyn,
      accountName: userContext?.account_name,
      roleNames: (userContext?.roles || []).map((r) => r.role_name).filter(Boolean),
      isOwner,
      isSubAccountMember,
      approvalPolicy: userContext?.approval_policy,
      modules,
      canOpen,
      canAny,
      goTo,
      jumpToStep,
      subscriptionPath: isSubAccountMember ? '/account/subscription/sub-account' : '/account/subscription',
      walletPath: isSubAccountMember ? '/account/wallet/sub-account' : '/account/wallet',
    }),
    [orgName, pricingUserId, showBlyn, userContext, isOwner, isSubAccountMember, modules, canOpen, canAny, goTo, jumpToStep]
  );

  const steps: TourStep[] = useMemo(() => {
    const list: TourStep[] = [
      { id: 'welcome', section: 'Getting started', label: 'Welcome', icon: 'solar:hand-shake-linear', render: () => <WelcomeStep ctx={ctx} /> },
      { id: 'organization', section: 'Getting started', label: 'Organization', icon: 'mdi:domain', render: () => <OrganizationStep ctx={ctx} /> },
      { id: 'team', section: 'Getting started', label: 'Team & Access', icon: 'mdi:shield-account-outline', render: () => <TeamStep ctx={ctx} /> },
      { id: 'subscription', section: 'Getting started', label: 'Subscription', icon: 'solar:card-linear', render: () => <SubscriptionStep ctx={ctx} /> },
      { id: 'wallet', section: 'Getting started', label: 'Wallet', icon: 'solar:wallet-linear', render: () => <WalletStep ctx={ctx} /> },
      ...(canAny(['integration.view', 'integration.edit'])
        ? [{ id: 'pixel', section: 'Getting started' as const, label: 'Tracking pixel', icon: 'mdi:radar', render: () => <PixelStep ctx={ctx} /> }]
        : []),
      { id: 'workflow', section: 'Modules', label: 'How it fits together', icon: 'solar:routing-2-linear', render: () => <WorkflowStep ctx={ctx} /> },
      ...modules.map<TourStep>((m) => ({
        id: `module-${m.key}`,
        section: 'Modules',
        label: FAMILIES[m.key].label,
        icon: FAMILIES[m.key].icon,
        color: FAMILIES[m.key].color,
        render: () => <ModuleStep ctx={ctx} group={m} />,
      })),
      { id: 'settings', section: 'Modules', label: 'Account settings', icon: 'solar:settings-linear', render: () => <AccountSettingsStep ctx={ctx} /> },
      { id: 'finish', section: 'Finish', label: 'Next steps', icon: 'solar:checklist-minimalistic-linear', render: () => <FinishStep ctx={ctx} /> },
    ];
    return list;
  }, [ctx, modules, canAny]);
  stepsRef.current = steps;

  const safeIndex = Math.min(stepIndex, steps.length - 1);
  const step = steps[safeIndex];
  const isFirst = safeIndex === 0;
  const isLast = safeIndex === steps.length - 1;

  useEffect(() => {
    setVisited((v) => (v.has(safeIndex) ? v : new Set(v).add(safeIndex)));
    scrollRef.current?.scrollTo({ top: 0 });
  }, [safeIndex]);

  const searchEntries = useMemo(
    () =>
      buildSearchIndex({
        steps: steps.map((s) => ({ id: s.id, label: s.label })),
        modules,
        isOwner,
        showBlyn,
      }),
    [steps, modules, isOwner, showBlyn]
  );
  const searching = query.trim().length > 0;
  const results = useMemo(() => (searching ? searchIndex(searchEntries, query) : []), [searching, searchEntries, query]);
  const stepLabel = useCallback((id: string) => steps.find((s) => s.id === id)?.label ?? '', [steps]);

  const pickResult = useCallback(
    (r: SearchEntry) => {
      const i = steps.findIndex((s) => s.id === r.stepId);
      if (i < 0) return;
      setQuery('');
      setStepIndex(i);
      setFocus(r.kind === 'Step' ? null : { title: r.title, nonce: Date.now() });
    },
    [steps]
  );

  // After jumping from a search result, scroll the matching item into view and flash it.
  useEffect(() => {
    if (!focus || searching) return;
    const t = window.setTimeout(() => {
      const root = scrollRef.current;
      const el = root?.querySelector<HTMLElement>(`[data-search-title="${CSS.escape(focus.title)}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.animate(
        [
          { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.9)}` },
          { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.9)}`, offset: 0.6 },
          { boxShadow: '0 0 0 0 transparent' },
        ],
        { duration: 2200, easing: 'ease-out' }
      );
    }, 320); // after the step's fade-in
    return () => window.clearTimeout(t);
  }, [focus, searching, safeIndex, theme.palette.primary.main]);

  const next = useCallback(() => {
    setFocus(null);
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [steps.length]);
  const back = useCallback(() => {
    setFocus(null);
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (['INPUT', 'TEXTAREA'].includes(target.tagName)) return;
    if (e.key === 'ArrowRight') next();
    if (e.key === 'ArrowLeft') back();
  };

  if (!open || !eligible) return null;

  const progress = Math.round((visited.size / steps.length) * 100);
  const sections = ['Getting started', 'Modules', 'Finish'] as const;

  const searchField = (
    <TextField
      inputRef={searchRef}
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      onKeyDown={(e) => {
        // Esc clears the search first instead of closing the whole tour.
        if (e.key === 'Escape' && query) {
          e.stopPropagation();
          setQuery('');
        }
        if (e.key === 'Enter' && results[0]) pickResult(results[0]);
      }}
      placeholder="Search the tour…"
      size="small"
      fullWidth
      inputProps={{ 'aria-label': 'Search the tour' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Iconify icon="solar:magnifer-linear" width={18} />
          </InputAdornment>
        ),
        endAdornment: query ? (
          <InputAdornment position="end">
            <IconButton size="small" aria-label="Clear search" onClick={() => setQuery('')} edge="end">
              <Iconify icon="mingcute:close-line" width={16} />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );

  const rail = (
    <Box
      sx={{
        width: 260,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box sx={{ px: 2.5, pt: 3, pb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Iconify icon="solar:map-arrow-right-linear" width={22} sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={700}>
            Platform tour
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.primary" sx={{ display: 'block', mt: 1.5, mb: 0.75 }}>
          {visited.size} of {steps.length} explored
        </Typography>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
        <Box sx={{ mt: 2 }}>{searchField}</Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        {sections.map((section) => (
          <Box key={section} sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ display: 'block', px: 1, py: 0.75, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              {section}
            </Typography>
            {steps.map((s, i) =>
              s.section !== section ? null : (
                <Stack
                  key={s.id}
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  role="button"
                  tabIndex={0}
                  aria-current={i === safeIndex ? 'step' : undefined}
                  onClick={() => {
                    setQuery('');
                    setFocus(null);
                    setStepIndex(i);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    setQuery('');
                    setFocus(null);
                    setStepIndex(i);
                  }}
                  sx={{
                    px: 1,
                    py: 0.875,
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: i === safeIndex ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                    '&:hover, &:focus-visible': { bgcolor: alpha(theme.palette.primary.main, i === safeIndex ? 0.14 : 0.06), outline: 'none' },
                  }}
                >
                  <Iconify icon={s.icon} width={18} sx={{ color: s.color ?? 'primary.main', flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={i === safeIndex ? 700 : 500} sx={{ flex: 1 }} noWrap>
                    {s.label}
                  </Typography>
                  {visited.has(i) && i !== safeIndex && (
                    <Iconify icon="solar:check-circle-bold" width={16} sx={{ color: 'success.main', flexShrink: 0 }} />
                  )}
                </Stack>
              )
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      // A stray backdrop click shouldn't throw away a long tour; Esc, X and Skip still close it.
      onClose={(_, reason) => reason !== 'backdropClick' && close()}
      onKeyDown={onKeyDown}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      aria-label="Platform tour"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2.5,
          height: fullScreen ? '100%' : 'min(780px, 92vh)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
        },
      }}
    >
      {isDesktop && rail}

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: { xs: 2, sm: 4 }, pt: 2, pb: isDesktop ? 0 : 1.5, borderBottom: isDesktop ? 0 : 1, borderColor: 'divider' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {!isDesktop && (
              <>
                <Typography variant="caption" color="text.primary">
                  Step {safeIndex + 1} of {steps.length} · {step.label}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={((safeIndex + 1) / steps.length) * 100}
                  sx={{ mt: 0.75, height: 4, borderRadius: 2 }}
                />
                <Box sx={{ mt: 1.5 }}>{searchField}</Box>
              </>
            )}
          </Box>
          <IconButton size="small" onClick={close} aria-label="Close tour">
            <Iconify icon="mingcute:close-line" width={20} />
          </IconButton>
        </Stack>

        <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 4 }, pt: { xs: 2, md: 0.5 }, pb: 4 }}>
          {searching ? (
            <Box sx={{ pt: { md: 2.5 } }}>
              <SearchResults
                query={query}
                results={results}
                stepLabel={stepLabel}
                onPick={pickResult}
                onSuggest={(q) => {
                  setQuery(q);
                  searchRef.current?.focus();
                }}
              />
            </Box>
          ) : (
            <TourFocusContext.Provider value={focus?.title ?? null}>
              <Fade in key={`${step.id}-${focus?.nonce ?? 0}`} timeout={250}>
                <Box>{step.render()}</Box>
              </Fade>
            </TourFocusContext.Provider>
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          sx={{ px: { xs: 2, sm: 4 }, py: 1.75, borderTop: 1, borderColor: 'divider' }}
        >
          <Tooltip
            title={alreadyDismissed ? 'You have already turned off the automatic tour. You can still open it anytime from your account menu.' : ''}
            placement="top-start"
          >
            <FormControlLabel
              disabled={alreadyDismissed}
              control={<Checkbox size="small" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} />}
              label={
                <Typography variant="body2">
                  {alreadyDismissed ? 'Automatic tour turned off' : 'Don’t show this again'}
                </Typography>
              }
              sx={{ mr: 'auto' }}
            />
          </Tooltip>
          {!isLast && (
            <CustomButton variant="outlined" onClick={close}>
              Skip
            </CustomButton>
          )}
          {!isFirst && (
            <CustomButton variant="outlined" onClick={back} startIcon={<Iconify icon="solar:arrow-left-linear" width={18} />}>
              Back
            </CustomButton>
          )}
          <CustomButton
            variant="contained"
            onClick={isLast ? close : next}
            endIcon={!isLast ? <Iconify icon="solar:arrow-right-linear" width={18} /> : undefined}
          >
            {isLast ? 'Get started' : `Next: ${steps[safeIndex + 1]?.label}`}
          </CustomButton>
        </Stack>
      </Box>
    </Dialog>
  );
}
